import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { fetchNoteTypes, postNote, postNoteDocument, postSummary } from "../../../../services/httpClient";
import Contact from "./Contact";
import Intervention from "./Intervention";
import Referral from "./Referral";
import Standard from "./Standard";
import StudentConnections from "./StudentConnections";
import useApp from "../../../../utils/useApp";
import { User } from "../../../../types/user";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";
import Infraction from "./Infraction";
import CustomNote from "./CustomNote";

interface CreateNoteState {
  fetchNotes?: any;
  setViewNotes: any;
  setCreateNote: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  activeFamily: User[];
  saveIsEnabled: boolean;
  setSaveIsEnabled: any;
  setUserToSave: any;
  setSummary: any;
}

const CreateNote = ({
  fetchNotes,
  setViewNotes,
  setCreateNote,
  setShowToast,
  setToastText,
  setToastBody,
  activeFamily,
  saveIsEnabled,
  setSaveIsEnabled,
  setUserToSave,
  setSummary,
}: CreateNoteState) => {
  const { id } = useParams();
  const { showMenu }: any = useApp();
  const [familyUser, setFamilyUser] = useState<string>("unassigned");
  const [type, setType] = useState<string>("");
  const [typeObject, setTypeObject] = useState<any>({});
  const [newNote, setNewNote] = useState<any>({});
  const [docs, setDocs] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    getTypes();
    const realFamily = activeFamily.filter((m) => m.name !== "Unassigned");

    if (realFamily.length === 1) {
      setFamilyUser(realFamily[0].id + "|" + realFamily[0].type);
    }
  }, []);

  const getTypes = () => {
    fetchNoteTypes()
      .then((res: any) => {
        var types = res[0].data.filter((n: any) => n?.type !== "system");
        types = types.filter((t: any) => t?.is_published === 1);
        types = types.filter((n: any) => n?.is_archived !== 1);

        var sorted = [...types];
        sorted.sort((a: any, b: any) => a.is_archived - b.is_archived);

        setTypes(sorted);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const updateType = (e: any) => {
    setType(e?.key ?? "");
    var fullType = types.find((t: any) => t.key === e?.key);
    setTypeObject(fullType);
    setSaveIsEnabled(false);
    setUserToSave(false);
  };

  const onClickSave = () => {
    setErrMsg("");
    postNote(newNote, id)
      .then((res: any) => {
        //Special code for Standard type notes
        const noteId = res[0].data.note_id;
        if (newNote.notetype === "standard" && docs.length > 0) {
          const length = docs.length;
          var counter = 0;
          docs.map((d: any) => {
            counter++;
            const name = d.name;
            const data = d.data;

            postNoteDocument(name, data, id, noteId)
              .then((res: any) => {
                if (counter === length) {
                  fetchNotes();
                  setViewNotes(true);
                  setCreateNote(false);
                  setUserToSave(false);
                  setShowToast(true);
                  setToastText("Note Created");
                  setToastBody(title(newNote.notetype));
                }
              })
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg || "Failed to save note document");
              });
          });
        } else {
          fetchNotes();
          setViewNotes(true);
          setCreateNote(false);
          setUserToSave(false);
          setShowToast(true);
          setToastText("Note Created");
          setToastBody(newNote?.type === "custom" ? newNote.notetypename : title(newNote.notetype));
        }

        postSummary(id)
          .then((res: any) => {
            var magic = res[0].data?.summary?.postResponse;
            setSummary(magic);
          })
          .catch((err: any) => {
            console.log(err);
          });
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg || "Failed to save note");
      });
  };

  const title = (type: any) => {
    if (type === "student") {
      return "Student Connections";
    } else if (type === "intervention") {
      return "Intervention Follow-Up";
    } else {
      return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  return (
    <>
      <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
        {errMsg}
      </p>
      <Card className={showMenu ? "bg-light sticky-top card-note-menu-open-sticky" : "bg-light sticky-top card-note-sticky"}>
        <Row className="pe-0 mb-3 mt-3">
          <Col className="col-6">
            <SelectObjectTypeahead label="Note Type" value={type} options={types} onUpdate={(e: any) => updateType(e)} />
          </Col>
          <Col className="col-4"></Col>
          <Col className="col-2 d-flex justify-content-end align-items-center">
            <Button className="app-secondary-color button" onClick={() => onClickSave()} disabled={!saveIsEnabled}>
              Save
            </Button>
          </Col>
        </Row>
      </Card>
      {type === "standard" && (
        <Standard
          activeFamily={activeFamily}
          familyUser={familyUser}
          setFamilyUser={setFamilyUser}
          documents={docs}
          setDocuments={setDocs}
          setUpdatedNote={setNewNote}
          setSaveIsEnabled={setSaveIsEnabled}
          setUserToSave={setUserToSave}
          myCase={true}
        />
      )}
      {type === "contact" && (
        <Contact
          activeFamily={activeFamily}
          familyUser={familyUser}
          setFamilyUser={setFamilyUser}
          setUpdatedNote={setNewNote}
          setSaveIsEnabled={setSaveIsEnabled}
          setUserToSave={setUserToSave}
          myCase={true}
        />
      )}
      {type === "intervention" && (
        <Intervention
          activeFamily={activeFamily}
          familyUser={familyUser}
          setFamilyUser={setFamilyUser}
          setUpdatedNote={setNewNote}
          setSaveIsEnabled={setSaveIsEnabled}
          setUserToSave={setUserToSave}
          myCase={true}
        />
      )}
      {type === "referral" && (
        <Referral
          activeFamily={activeFamily}
          familyUser={familyUser}
          setFamilyUser={setFamilyUser}
          setUpdatedNote={setNewNote}
          setSaveIsEnabled={setSaveIsEnabled}
          setUserToSave={setUserToSave}
          myCase={true}
        />
      )}
      {type === "student" && (
        <StudentConnections
          activeFamily={activeFamily}
          familyUser={familyUser}
          setFamilyUser={setFamilyUser}
          setUpdatedNote={setNewNote}
          setSaveIsEnabled={setSaveIsEnabled}
          setUserToSave={setUserToSave}
          myCase={true}
        />
      )}
      {type === "infraction" && (
        <Infraction
          activeFamily={activeFamily}
          familyUser={familyUser}
          setFamilyUser={setFamilyUser}
          noteType={typeObject}
          setUpdatedNote={setNewNote}
          setSaveIsEnabled={setSaveIsEnabled}
          setUserToSave={setUserToSave}
          myCase={true}
        />
      )}
      {/^\d+_/.test(type) && (
        <CustomNote
          key={typeObject?.key}
          activeFamily={activeFamily}
          familyUser={familyUser}
          setFamilyUser={setFamilyUser}
          type={typeObject}
          setUpdatedNote={setNewNote}
          setSaveIsEnabled={setSaveIsEnabled}
          setUserToSave={setUserToSave}
          myCase={true}
        />
      )}
    </>
  );
};

export default CreateNote;
